import React from 'react'
import { Check, Clock, UserPlus, Mail, CreditCard, Bell, MessageSquare, Handshake } from 'lucide-react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { AdminNotification, getEventConfig } from '../../../types/adminNotifications'

interface AdminNotificationItemProps {
  notification: AdminNotification
  onMarkAsRead: (id: string) => void
  /** Chamado ao clicar no título quando `config.href` existe — o dropdown do sino
   *  fica montado entre rotas, então sem isso a navegação não o fecha. */
  onNavigate?: () => void
}

const iconMap: Record<string, React.ReactNode> = {
  UserPlus: <UserPlus className="w-5 h-5" />,
  Mail: <Mail className="w-5 h-5" />,
  CreditCard: <CreditCard className="w-5 h-5" />,
  Clock: <Clock className="w-5 h-5" />,
  Bell: <Bell className="w-5 h-5" />,
  MessageSquare: <MessageSquare className="w-5 h-5" />,
  Handshake: <Handshake className="w-5 h-5" />,
}

const getTimeAgo = (dateString: string) => {
  const now = new Date()
  const date = new Date(dateString)
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) return 'Agora'
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`
  return `${Math.floor(diffInMinutes / 1440)}d atrás`
}

const AdminNotificationItem: React.FC<AdminNotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onNavigate,
}) => {
  const config = getEventConfig(notification.event_type)
  const icon = iconMap[config.icon] || iconMap.Bell

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`p-3 sm:p-4 border-b border-cream-300 dark:border-ink-500 hover:bg-cream-100 dark:hover:bg-ink-900/40 transition-colors ${
        !notification.is_read ? 'bg-terra-50 dark:bg-terra-500/10' : ''
      }`}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        <div className={`flex-shrink-0 mt-1 p-1.5 rounded-full ${config.bgColor}`}>
          <div className={config.color}>
            {icon}
          </div>
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {(() => {
                  const titleEl = (
                    <h4 className={`text-sm font-medium break-words ${
                      !notification.is_read ? 'text-ink-900 dark:text-cream-50' : 'text-ink-700 dark:text-ink-300'
                    }`}>
                      {notification.title}
                    </h4>
                  )
                  return config.href ? (
                    <Link to={config.href} className="hover:underline" onClick={onNavigate}>
                      {titleEl}
                    </Link>
                  ) : (
                    titleEl
                  )
                })()}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${config.bgColor} ${config.color}`}>
                  {config.label}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-ink-500 dark:text-ink-400 mt-1 break-words whitespace-normal">
                {notification.message}
              </p>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {!notification.is_read && (
                <button
                  onClick={() => onMarkAsRead(notification.id)}
                  className="p-1 hover:bg-cream-200 dark:hover:bg-ink-500/40 rounded-caderno-xs transition-colors"
                  title="Marcar como lida"
                >
                  <Check className="w-3 h-3 text-ink-400 dark:text-ink-500" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 mt-2">
            <Clock className="w-3 h-3 text-ink-400 dark:text-ink-500 flex-shrink-0" />
            <span className="text-xs text-ink-400 dark:text-ink-500 truncate">
              {getTimeAgo(notification.created_at)}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default AdminNotificationItem
